import { Modal } from "antd";
import { useState, useEffect, useCallback } from "react";
import {
  PlusOutlined,
  MinusOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useMenuItems, useMenuModifiers } from "../../../../../hooks/useMenu";
import type { BillOut } from "../../../../../api/bills";
import { useViewport } from "../../../../../hooks/useViewport";
import { useAppSelector } from "../../../../../store/hooks";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

export interface CancelItemsSelection {
  elements: Array<{
    billElementId: number;
    billElementItemId: number;
    quantity: number;
  }>;
  reason: string;
  isDefective: boolean;
}

/**
 * Изходът на сторното, както го връща извикващият.
 *
 * Дотук `onConfirm` беше `Promise<void>` и прозорецът се затваряше при ВСЯКО
 * връщане — включително когато извикващият е хванал грешката, показал я е с
 * toast и е върнал нормално (`TableFooter.handleCancelItems`). Тоест при
 * пропаднало сторно прозорецът се затваряше като при успех, селекцията се
 * губеше, а от частично минала серия („три реда, четвъртият гръмна") не
 * оставаше нищо на екрана.
 *
 * Сега има три отговора и трите са различни: изключение (нищо не се знае),
 * `{ ok: false }` (знае се, че е пропаднало) и всичко останало (успех).
 */
export interface CancelItemsOutcome {
  ok: boolean;
  /** Какво да се покаже В ПРОЗОРЕЦА; липсва → общият текст. */
  error?: string;
}

interface CancelItemsModalProps {
  open: boolean;
  onClose: () => void;
  // Може да върне Promise — потвърждението изчаква сторното, преди да
  // затвори прозореца (виж `handleConfirm`).
  onConfirm: (
    data: CancelItemsSelection,
  ) => void | CancelItemsOutcome | Promise<void | CancelItemsOutcome>;
  bill: BillOut | undefined;
}

interface SelectedQuantities {
  [billElementItemId: number]: number;
}

// Сторно на продукти — списък с ВСИЧКИ поръчани продукти (както „Раздели"),
// избира се кои и по колко; не изисква предварително селектиран ред.
// Бекендът получава ЕДНА `/cancel-items` заявка с всички избрани редове (пълно
// или частично количество) и печата ЕДИН кухненски билет на станция.
export const CancelItemsModal = ({
  open,
  onClose,
  onConfirm,
  bill,
}: CancelItemsModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const allItems = useMenuItems();
  const allModifiers = useMenuModifiers();
  const [selectedQuantities, setSelectedQuantities] =
    useState<SelectedQuantities>({});
  // Предефинирана причина (chip) — null = нищо не е избрано (не е задължителна);
  // „other" отваря свободния текст в `reason`.
  const [reasonKey, setReasonKey] = useState<CancelReasonKey | null>(null);
  const [reason, setReason] = useState("");
  const [isDefective, setIsDefective] = useState(false);
  // В режим „със склад" продаваемото е рецепта → друго заглавие.
  const usesWarehouse = useAppSelector(
    (state) => state.auth.user?.uses_warehouse === true,
  );
  // Пази срещу второ потвърждение, докато сторното е в полет.
  const [submitting, setSubmitting] = useState(false);
  // Пропаднало сторно: текстът остава В ПРОЗОРЕЦА, докато човекът не опита
  // пак или не затвори сам.
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedQuantities({});
      setReasonKey(null);
      setReason("");
      setIsDefective(false);
      setFailure("");
    }
  }, [open]);

  const getTranslation = (
    translations: Array<{ languageCode: string; name: string }>,
    code = i18n.language,
  ) => translations.find((tr) => tr.languageCode === code)?.name || "";

  // Сериализацията на BE връща само НЕсторнираните редове, така че всичко
  // тук е активно и може да се сторнира.
  const billItems =
    bill?.elements.flatMap((el) =>
      el.items.map((item) => ({ ...item, billElementId: el.id })),
    ) || [];

  const handleQuantityChange = (itemId: number, delta: number) => {
    const item = billItems.find((i) => i.id === itemId);
    if (!item) return;
    const maxQty = parseFloat(item.quantity);
    const currentQty = selectedQuantities[itemId] || 0;
    const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));
    setSelectedQuantities((prev) => ({ ...prev, [itemId]: newQty }));
  };

  const selectedItems = billItems.filter(
    (item) => (selectedQuantities[item.id] || 0) > 0,
  );

  // „Избери всичко" — изведено от селекцията: маха ли се количество от ред,
  // чекбоксът сам угасва. Повторно натискане чисти цялата селекция.
  const allSelected =
    billItems.length > 0 &&
    billItems.every(
      (item) => (selectedQuantities[item.id] || 0) >= parseFloat(item.quantity),
    );

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedQuantities({});
    } else {
      setSelectedQuantities(
        Object.fromEntries(
          billItems.map((item) => [item.id, parseFloat(item.quantity)]),
        ),
      );
    }
  };

  // Цена на ред (продукт + добавки) — добавките не са в item.total_sum.
  // Крайните им суми се четат от самите тях: отстъпката върху продукт ги
  // обхваща (BE ги преизчислява), промяната на цената — не.
  const getLineTotal = (item: (typeof billItems)[number]) => {
    const additionsTotal = item.additions.reduce(
      (sum, a) => sum + parseFloat(a.total_sum),
      0,
    );
    return parseFloat(item.total_sum) + additionsTotal;
  };

  // Сторното е необратимо: всяко второ натискане е второ сторно и втори
  // кухненски билет. Досега прозорецът се затваряше веднага след `onConfirm` —
  // заявката още тръгваше, бутонът беше активен, а бързото двойно натискане
  // (или два пъти Enter) я пускаше два пъти. Сега потвърждението ЧАКА изхода и
  // едва тогава затваря.
  // Причината за BE: етикетът на избрания chip; при „Друго" — свободният текст.
  const reasonText =
    reasonKey === null
      ? ""
      : reasonKey === "other"
        ? reason
        : t(CANCEL_REASON_LABEL_KEYS[reasonKey]);

  const handleConfirm = useCallback(async () => {
    if (submitting || selectedItems.length === 0) return;
    setSubmitting(true);
    setFailure("");
    try {
      const outcome = await onConfirm({
        elements: selectedItems.map((item) => ({
          billElementId: item.billElementId,
          billElementItemId: item.id,
          quantity: selectedQuantities[item.id],
        })),
        reason: reasonText,
        isDefective,
      });
      // Пропадналото сторно ОСТАВЯ прозореца отворен със селекцията —
      // операторът вижда какво е останало и решава сам. Затварянето при
      // провал изглеждаше точно като успех.
      if (outcome && outcome.ok === false) {
        setFailure(outcome.error || t("cancelItem.failed"));
        return;
      }
      onClose();
    } catch (err: unknown) {
      // Изключение = изходът е НЕИЗВЕСТЕН (сторното може вече да е записано
      // и билетът — излязъл в кухнята). Нито се затваря, нито се твърди, че не
      // е станало — казва се какво се знае.
      setFailure(
        err instanceof Error && err.message
          ? t("cancelItem.failedReason", { reason: err.message })
          : t("cancelItem.failed"),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    selectedItems,
    selectedQuantities,
    reasonText,
    isDefective,
    onConfirm,
    onClose,
    t,
  ]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      ) {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleConfirm, onClose, isDesktop]);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPadClass = isNarrow ? "px-4" : "px-6";
  const billTotal = parseFloat(bill?.total_amount ?? "0");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<DeleteOutlined />}
          title={t(
            usesWarehouse
              ? "cancelItem.titleRecipes"
              : "cancelItem.titleAssortments",
          )}
          subtitle={t("cancelItem.subtitle", {
            name: bill?.name ?? "",
            id: bill?.id ?? "",
            total: billTotal.toFixed(2),
          })}
          onClose={onClose}
          isNarrow={isNarrow}
          subtitleBelowOnNarrow
        />

        <div
          className={`${bodyPadClass} flex min-h-0 flex-col gap-2 overflow-y-auto pt-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
        >
          {billItems.map((item) => {
            const itemName =
              item.item_names?.[i18n.language] ||
              item.item_names?.["bg"] ||
              Object.values(item.item_names || {})[0] ||
              t("common.product");
            const maxQty = parseFloat(item.quantity);
            const selectedQty = selectedQuantities[item.id] || 0;
            const unitPrice = getLineTotal(item) / parseFloat(item.quantity);
            const active = selectedQty > 0;

            return (
              <div
                key={item.id}
                className={`rounded-[10px] border px-3 py-2 transition-colors ${
                  active
                    ? "border-pos-accent/30 bg-pos-marked"
                    : "border-pos-line3 bg-pos-panel"
                }`}
              >
                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex min-w-0 flex-1 items-baseline gap-2">
                    <span className="min-w-0 truncate text-[13.5px] font-semibold text-pos-ink">
                      {itemName}
                    </span>
                    <span className="shrink-0 font-pos-mono text-[11.5px] text-pos-muted">
                      {unitPrice.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <QtyBtn
                      icon={<MinusOutlined />}
                      onClick={() => handleQuantityChange(item.id, -1)}
                      disabled={selectedQty === 0}
                    />
                    {/* Избраното количество, а под него „от N" (наличното) */}
                    <div className="flex min-w-[48px] flex-col items-center leading-none">
                      <span
                        className={`font-pos-mono text-[15px] font-bold tabular-nums ${
                          active ? "text-pos-accent-ink" : "text-pos-ink"
                        }`}
                      >
                        {selectedQty}
                      </span>
                      <span className="mt-0.5 font-pos-mono text-[10px] text-pos-muted">
                        {t("cancelItem.ofQuantity", { max: maxQty })}
                      </span>
                    </div>
                    <QtyBtn
                      icon={<PlusOutlined />}
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={selectedQty >= maxQty}
                    />
                  </div>
                </div>

                {(item.modifiers.length > 0 || item.additions.length > 0) && (
                  <div className="mt-1.5 ml-2 flex flex-col gap-0.5 border-l-2 border-pos-line pl-2.5 text-[11px] text-pos-soft">
                    {item.modifiers.length > 0 && (
                      <div>
                        {t("splitBill.modifiersLabel")}{" "}
                        {item.modifiers
                          .map((m) => {
                            const mod = allModifiers.find(
                              (modifier) =>
                                modifier.id.toString() === m.modifier_id,
                            );
                            return mod ? getTranslation(mod.translations) : "";
                          })
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                    {item.additions.length > 0 && (
                      <div>
                        {t("splitBill.addonsLabel")}{" "}
                        {item.additions
                          .map((a) => {
                            const addonItem = allItems.find(
                              (i) => i.id === a.addition_item_id,
                            );
                            return addonItem
                              ? `${getTranslation(addonItem.itemLangs)} x${a.quantity}`
                              : "";
                          })
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* „Избери всичко" / „Брак" + причина (по избор) — общи за всички избрани редове */}
        <div
          className={`${bodyPadClass} flex shrink-0 flex-col border-t border-pos-line2 ${
            isNarrow ? "gap-2 py-2" : "gap-3 py-3"
          }`}
        >
          {isNarrow ? (
            // Телефон: двата бутона на свой ред, после „Причина"
            <>
              <div className="flex items-center justify-center gap-2">
                <CheckboxButton
                  checked={allSelected}
                  onToggle={handleToggleSelectAll}
                  label={t("cancelItem.selectAll")}
                />
                <CheckboxButton
                  checked={isDefective}
                  onToggle={() => setIsDefective((v) => !v)}
                  label={t("cancelItem.defective")}
                />
              </div>
              <span className="text-[13px] font-medium leading-none text-pos-muted">
                {t("cancelItem.reasonLabel")}
              </span>
            </>
          ) : (
            /* Десктоп: „Причина" вляво (същият текстов стил като бутоните), „Избери всичко"/„Брак" вдясно */
            <div className="flex items-end justify-between gap-2.5">
              <span className="text-[13px] font-medium leading-none text-pos-muted">
                {t("cancelItem.reasonLabel")}
              </span>
              <div className="flex items-center gap-2">
                <CheckboxButton
                  checked={allSelected}
                  onToggle={handleToggleSelectAll}
                  label={t("cancelItem.selectAll")}
                />
                <CheckboxButton
                  checked={isDefective}
                  onToggle={() => setIsDefective((v) => !v)}
                  label={t("cancelItem.defective")}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {CANCEL_REASON_KEYS.map((key) => (
                <ReasonChip
                  key={key}
                  label={t(CANCEL_REASON_LABEL_KEYS[key])}
                  active={reasonKey === key}
                  // Повторно натискане маха избора — причината не е задължителна
                  onClick={() => setReasonKey(reasonKey === key ? null : key)}
                />
              ))}
            </div>
            {reasonKey === "other" && (
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={1}
                placeholder={t("cancelItem.reasonPlaceholder")}
                className="w-full resize-none rounded-[10px] border border-pos-line3 bg-pos-card px-3.5 py-2.5 text-base text-pos-ink outline-none placeholder:text-pos-muted focus:border-pos-accent md:text-sm"
              />
            )}
          </div>
        </div>

        {failure && (
          <div
            role="alert"
            className={`${bodyPadClass} pb-2 text-[12.5px] font-medium leading-snug text-pos-danger-ink`}
          >
            {failure}
          </div>
        )}

        <ModalFooter isNarrow={isNarrow} columns="1fr 3fr">
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("cancelItem.confirmButton")}
            onClick={handleConfirm}
            disabled={selectedItems.length === 0}
            loading={submitting}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

// Предефинираните причини — chips; „Брак" НЕ е тук, той е отделният флаг
// is_defective (бутонът „Брак"). Текстът към BE е преведеният етикет.
const CANCEL_REASON_KEYS = [
  "wrongMark",
  "customerRefused",
  "orderChanged",
  "other",
] as const;
type CancelReasonKey = (typeof CANCEL_REASON_KEYS)[number];
const CANCEL_REASON_LABEL_KEYS: Record<CancelReasonKey, string> = {
  wrongMark: "cancelItem.reasonWrongMark",
  customerRefused: "cancelItem.reasonCustomerRefused",
  orderChanged: "cancelItem.reasonOrderChanged",
  other: "cancelItem.reasonOther",
};

const ReasonChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`h-9 cursor-pointer whitespace-nowrap rounded-[9px] border px-2.5 text-[13px] font-semibold transition-colors ${
      active
        ? "border-pos-accent bg-pos-marked text-pos-accent-ink"
        : "border-pos-ctl bg-pos-card text-pos-ink2 hover:bg-pos-canvas"
    }`}
  >
    {label}
  </button>
);

// „Избери всичко" / „Брак" — бутон с чекбокс-квадратче вътре (същият като в
// „Раздели сметка"); квадратчето светва в accent при активно.
const CheckboxButton = ({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[9px] border border-pos-ctl bg-pos-card px-2.5 text-[13px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-canvas"
  >
    <span
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border text-white transition-colors ${
        checked
          ? "border-pos-accent bg-pos-accent"
          : "border-pos-check bg-pos-canvas"
      }`}
    >
      {checked && <CheckOutlined className="text-[10px]" />}
    </span>
    <span>{label}</span>
  </button>
);

const QtyBtn = ({
  icon,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  // Tailwind hover: (v4) важи само на устройства с истински hover — на телефон
  // докосването НЕ оцветява бутона (JS onMouseEnter „залепваше" фона след tap)
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-[34px] h-[34px] rounded-lg bg-pos-card border border-pos-ctl text-pos-soft2 inline-flex items-center justify-center text-xs cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-pos-marked enabled:hover:border-pos-accent/30 enabled:hover:text-pos-accent-ink"
  >
    {icon}
  </button>
);
